import { useEffect, useRef, useState } from 'react';
import { applyFormPatch, compileFormEdit, locateFormElement, type FormElementRef, type FormPatchOp, type RawFormEdit } from '@ayumia/component-runtime';
import type { FormEditProposalsResult } from '@ayumia/contracts';
import { ApiError } from '../../../api/client';
import { Icon } from '../../../design-system/icons';
import type { ProfilePanel } from '../../editor/canvas-types';
import type { Template } from '../layout-model';

/**
 * Assistant de l'éditeur des formulaires (ADR-146, addendum du 2026-10-22) : une consigne en langage
 * courant ; le Core propose des opérations lisibles, l'auteur applique ou ignore chacune. Une opération
 * appliquée entre dans l'historique d'annulation comme un geste de l'auteur, et sa marque voyage avec
 * l'enregistrement du brouillon. L'assistant ne publie jamais : la demande passe par la capacité d'IA du
 * Core (`ProposeFormEdits`), jamais par un appel du studio à un fournisseur.
 */

type Proposal = FormEditProposalsResult;
type Operation = NonNullable<Proposal['operations']>[number];
type UiAction = NonNullable<Proposal['ui_actions']>[number];
type Mark = { proposal_id: string; decision_ref: string; operation_id: string; summary: string; applied_at: string };

export interface AssistantPort {
  draft: () => Promise<{ draft_id: string; draft_version: number; body: Template } | null>;
  propose: (payload: { draft_id: string; draft_version: number; instruction: string; locale: string; selection?: FormElementRef; page_id?: string }) => Promise<string>;
  proposal: (proposalId: string) => Promise<Proposal>;
}

/**
 * Marques des opérations appliquées (studio-draft 1.1.0). Une marque n'est envoyée que si son effet est
 * encore là : annuler l'opération (Ctrl Z) la retire de l'enregistrement suivant.
 */
export class AssistantMarks {
  private readonly entries: Array<{ mark: Mark; present: (document: Template) => boolean }> = [];

  add(mark: Mark, present: (document: Template) => boolean): void {
    this.entries.push({ mark, present });
  }

  current(document: Template): Mark[] {
    return this.entries.filter((entry) => entry.present(document)).map((entry) => entry.mark);
  }
}

/** Poll de la proposition : toutes les 1,5 s, deux minutes au plus. */
const POLL_MS = 1500;
const POLL_LIMIT = 80;

/** Élément sélectionné sur le canevas, au format des opérations (`field:/sections/1/fields/0`, `section:x`, `page:y`). */
export function selectionRef(document: Template, selected: string): FormElementRef | undefined {
  if (selected.startsWith('section:')) return { element_kind: 'section', section_id: selected.slice(8) };
  if (selected.startsWith('page:')) return { element_kind: 'page', page_id: selected.slice(5) };
  const match = /^field:\/sections\/(\d+)\/fields\/(\d+)$/.exec(selected);
  if (match === null) return undefined;
  const section = document.sections[Number(match[1])];
  const entry = section?.fields[Number(match[2])];
  return section === undefined || entry === undefined ? undefined : { element_kind: 'field', section_id: section.section_id, field_id: entry.field.field_id };
}

/** Identifiant de sélection du canevas pour un élément désigné, ou `null`. */
export function selectionId(document: Template, ref: FormElementRef | undefined): string | null {
  if (ref === undefined) return null;
  if (ref.element_kind === 'section') return `section:${ref.section_id}`;
  if (ref.element_kind === 'page') return `page:${ref.page_id}`;
  const located = locateFormElement(document, ref);
  return located === null || ref.element_kind !== 'field' ? null : `field:${located.pointer}`;
}

/** L'effet d'une opération est-il encore dans le document ? (marque gardée ou retirée). */
function stillApplied(operation: Operation): (document: Template) => boolean {
  const target = operation.target as FormElementRef | undefined;
  if (target === undefined) return () => true;
  if (operation.kind === 'remove_element') return (document) => locateFormElement(document, target) === null;
  return (document) => locateFormElement(document, target) !== null;
}

/**
 * Applique une opération : son patch si le document est celui sur lequel elle a été proposée, sinon la
 * même opération recompilée sur le document actuel (même calcul que le serveur). `null` : plus applicable.
 */
export function applyOperation(document: Template, base: Template, operation: Operation, locale: string): Template | null {
  if (JSON.stringify(document) === JSON.stringify(base)) {
    try {
      return applyFormPatch(document, operation.patch as unknown as FormPatchOp[]);
    } catch {
      return null;
    }
  }
  const raw = {
    kind: operation.kind,
    ...(operation.target === undefined ? {} : { target: operation.target }),
    ...(operation.element === undefined ? {} : { element: operation.element }),
    ...(operation.position === undefined ? {} : { position: operation.position }),
    ...(operation.changes === undefined ? {} : { changes: operation.changes }),
    ...(operation.remove_fields === undefined ? {} : { remove_fields: operation.remove_fields }),
  } as unknown as RawFormEdit;
  const compiled = compileFormEdit(document, raw, { locale });
  return compiled.ok ? applyFormPatch(document, compiled.patch) : null;
}

const failureText = (error: unknown): string => {
  if (error instanceof ApiError && error.code === 'TENANT_ENTITLEMENT_MISSING') return 'Assistant non ouvert pour ce tenant.';
  if (error instanceof ApiError && error.code === 'POLICY_DENIED') return 'Assistant non autorisé pour votre rôle.';
  return error instanceof Error ? error.message : String(error);
};

interface PanelState {
  status: 'idle' | 'asking' | 'ready' | 'failed';
  message?: string;
  /** Phrase du serveur, en infobulle : l'écran dit l'état en un mot. */
  hint?: string;
  /** La même consigne peut être relancée telle quelle. */
  retry?: boolean;
  proposal?: Proposal;
  base?: Template;
  done: Record<string, 'applied' | 'ignored'>;
}

function AssistantView({ port, marks, locale, context }: { port: AssistantPort; marks: AssistantMarks; locale: string; context: Parameters<ProfilePanel<Template>['render']>[0] }) {
  const { document, apply, selected, select, openLibrary, save } = context;
  const [instruction, setInstruction] = useState('');
  const [state, setState] = useState<PanelState>({ status: 'idle', done: {} });
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  const ask = async (): Promise<void> => {
    const text = instruction.trim();
    if (text === '') return;
    setState({ status: 'asking', done: {} });
    try {
      // La demande vise le brouillon serveur : il est enregistré d'abord, et doit être celui de l'écran.
      await save();
      let draft = await port.draft();
      if (draft !== null && JSON.stringify(draft.body) !== JSON.stringify(document)) {
        await save();
        draft = await port.draft();
      }
      if (draft === null || JSON.stringify(draft.body) !== JSON.stringify(document)) throw new Error('Enregistrez le formulaire, puis redemandez.');
      const ref = selectionRef(document, selected);
      const id = await port.propose({ draft_id: draft.draft_id, draft_version: draft.draft_version, instruction: text, locale, ...(ref === undefined ? {} : { selection: ref }) });
      for (let turn = 0; turn < POLL_LIMIT && alive.current; turn += 1) {
        const proposal = await port.proposal(id);
        if (proposal.status === 'ready') {
          setState({ status: 'ready', proposal, base: draft.body, done: {} });
          return;
        }
        if (proposal.status === 'failed') {
          // Génération épuisée (`GENERATION_FAILED`) ou brouillon changé : un mot, la phrase en infobulle, et relancer.
          setState({ status: 'failed', message: 'Échec', ...(proposal.failure?.message === undefined ? {} : { hint: proposal.failure.message }), retry: true, done: {} });
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      }
      if (alive.current) setState({ status: 'failed', message: 'Sans réponse', hint: 'L’assistant tarde à répondre.', retry: true, done: {} });
    } catch (error) {
      if (alive.current) setState({ status: 'failed', message: failureText(error), done: {} });
    }
  };

  const applyOne = (operation: Operation): void => {
    const { proposal, base } = state;
    if (proposal === undefined || base === undefined || proposal.decision_id === undefined) return;
    const next = applyOperation(document, base, operation, locale);
    if (next === null) {
      setState((current) => ({ ...current, message: `« ${operation.summary} » ne s’applique plus : redemandez.` }));
      return;
    }
    marks.add({ proposal_id: String(proposal.proposal_id), decision_ref: String(proposal.decision_id), operation_id: operation.operation_id, summary: operation.summary, applied_at: new Date().toISOString() }, stillApplied(operation));
    apply(next);
    const target = selectionId(next, operation.target as FormElementRef | undefined);
    if (target !== null && operation.kind !== 'remove_element') select(target);
    setState((current) => ({ ...current, done: { ...current.done, [operation.operation_id]: 'applied' } }));
  };

  const runUi = (action: UiAction): void => {
    if (action.kind === 'select') {
      const target = selectionId(document, action.target as FormElementRef | undefined);
      if (target !== null) select(target);
    } else if (action.destination === 'editor.library' || action.destination === 'editor.library.presets') openLibrary(null);
  };

  const proposal = state.proposal;
  const operations = proposal?.operations ?? [];
  return (
    <div className="ay-forms-assistant">
      <form
        className="ay-forms-assistant__ask"
        onSubmit={(event) => {
          event.preventDefault();
          void ask();
        }}
      >
        <textarea
          aria-label="Consigne"
          placeholder="Ex. : ajoute le poids brut sous la température"
          value={instruction}
          maxLength={1000}
          rows={3}
          onChange={(event) => setInstruction(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
              event.preventDefault();
              void ask();
            }
          }}
        />
        <button type="submit" className="ay-button ay-button--primary" disabled={state.status === 'asking' || instruction.trim() === ''} title="Ctrl Entrée">
          <Icon name="sparkle" size={16} />
          {state.status === 'asking' ? 'Réflexion…' : 'Demander'}
        </button>
      </form>
      {state.status === 'failed' && (
        <div className="ay-forms-assistant__line">
          <p className="ay-forms-assistant__line is-error" role="alert" title={state.hint}>
            <Icon name="alert" size={12} />
            {state.message}
          </p>
          {state.retry === true && (
            <button type="button" className="ay-button ay-button--secondary" disabled={instruction.trim() === ''} onClick={() => void ask()} title={state.hint}>
              Réessayer
            </button>
          )}
        </div>
      )}
      {state.status === 'ready' && state.message !== undefined && <p className="ay-forms-assistant__line is-error" role="alert">{state.message}</p>}
      {state.status === 'ready' && operations.length === 0 && (proposal?.refusals ?? []).length === 0 && <p className="ay-forms-assistant__line">Aucune proposition.</p>}
      {operations.length > 0 && (
        <ul className="ay-forms-assistant__operations" aria-label="Propositions">
          {operations.map((operation) => {
            const done = state.done[operation.operation_id];
            return (
              <li key={operation.operation_id} className={done === undefined ? undefined : `is-${done}`}>
                <button type="button" className="ay-forms-assistant__summary" onClick={() => { const target = selectionId(document, operation.target as FormElementRef | undefined); if (target !== null) select(target); }} title="Voir sur le canevas">
                  <Icon name="sparkle" size={14} />
                  <span>{operation.summary}</span>
                </button>
                {(operation.warnings ?? []).map((warning) => (
                  <span key={warning} className="ay-forms-assistant__warning" title={warning}>
                    <Icon name="alert" size={12} /> {warning}
                  </span>
                ))}
                {done === undefined ? (
                  <span className="ay-forms-assistant__actions">
                    <button type="button" className="ay-button ay-button--secondary" onClick={() => setState((current) => ({ ...current, done: { ...current.done, [operation.operation_id]: 'ignored' } }))}>Ignorer</button>
                    <button type="button" className="ay-button ay-button--primary" onClick={() => applyOne(operation)}>Appliquer</button>
                  </span>
                ) : (
                  <span className="ay-forms-assistant__state">{done === 'applied' ? 'Appliquée' : 'Ignorée'}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {(proposal?.refusals ?? []).map((refusal, index) => (
        <p key={`${refusal.code}-${index}`} className="ay-forms-assistant__line is-muted" title={refusal.instruction_excerpt}>
          <Icon name="info" size={12} /> {refusal.message}
        </p>
      ))}
      {(proposal?.ui_actions ?? []).map((action) => (
        <button key={action.action_id} type="button" className="ay-forms-assistant__ui" onClick={() => runUi(action)}>
          <Icon name={action.kind === 'select' ? 'pointer' : 'external-link'} size={14} /> {action.summary}
        </button>
      ))}
    </div>
  );
}

/** Panneau « Assistant » du rail de l'éditeur. */
export function assistantPanel(port: AssistantPort, marks: AssistantMarks, locale = 'fr'): ProfilePanel<Template> {
  return {
    id: 'assistant',
    label: 'Assistant',
    glyph: '✦',
    render: (context) => <AssistantView port={port} marks={marks} locale={locale} context={context} />,
  };
}
